import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { Observable, of, ReplaySubject } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { AuthService } from "src/app/core/services/auth.service";
import { environment } from "src/environments/environment";
import {
  DealAssociationSearchResponse,
  DealDetailType,
  DealListFilters,
  DealListFiltersMetaData,
  DealReferral,
  DealReferralTargetsResponse,
  Deals,
  DealsStatus,
  StatusActions,
  StatusValidation,
} from "../models/deals.model";
import { LeadHistory } from "../../leads/models/leads.model";
import { ProposalModel } from "../../properties/create-edit/proposals/models/proposals.model";
import {
  ExternalPropertyModel,
  ExternalPropertyPayloadModel,
} from "src/app/shared/models/external-property.model";

type DealWonActionOption = {
  id: number;
  name: string;
  translateKey?: string;
};

type DealWonActionResolution = {
  selectedActionId: number | null;
  action: DealWonActionOption | null;
  requiresSaleDetails: boolean;
  resolvedType: DealDetailType;
};

type DealListView = "progress" | "won" | "lost";

@Injectable({
  providedIn: "root",
})
export class DealsService {
  constructor(
    private http: HttpClient,
    private _cookiesService: CookieService,
    private authService: AuthService,
  ) {}

  private syncSubject = new ReplaySubject<void>(1);

  private getExternalPropertyTypeId(property: any): number | null {
    const propertyTypeId = Number(
      property?.propertyTypeGroupId ?? property?.propertyTypeId ?? NaN,
    );

    return Number.isFinite(propertyTypeId) ? propertyTypeId : null;
  }

  private getExternalPropertyTypeDescription(property: any): string | null {
    return (
      property?.propertyTypeGroup?.propertyTypeGroupTranslations?.[0]
        ?.description ??
      property?.propertyTypeGroup?.description ??
      property?.propertyType ??
      null
    );
  }

  private getExternalPropertyTitle(property: any): string {
    const propertyType =
      this.getExternalPropertyTypeDescription(property) ?? "";
    const typology = `${property?.typology ?? ""}`.trim();
    const title = [propertyType, typology].filter(Boolean).join(" ").trim();

    return title || property?.reference;
  }

  private mapLeadProperty(data: any): any {
    if (data?.property) {
      const businessType: "SALE" | "RENT" = [
        "To Lease",
        "For Vacations",
        "Local Accommodation",
      ].includes(data?.property?.propertyBusinessType?.description)
        ? "RENT"
        : "SALE";

      return {
        id: data?.property?.id,
        ref: data?.property?.reference,
        title: data?.property?.propertyTranslations[0]?.title,
        price: data?.property?.price,
        symbol: data?.property?.currency?.symbol,
        image: data?.property?.propertyFiles[0]?.fileName,
        store: data?.property?.store?.name,
        location: {
          address: data?.property?.propertyAddress[0]?.address,
          district: data?.property?.propertyAddress[0]?.district?.name,
          county: data?.property?.propertyAddress[0]?.county?.name,
          parish: data?.property?.propertyAddress[0]?.parish?.name,
        },
        businessType,
        yearBuilt: data?.property?.yearOfConstruction,
        propertyType:
          data?.property?.propertyType?.propertyTypeTranslations?.[0]
            ?.description,
        propertyType2:
          data?.property?.propertyType2?.propertyTypeTranslations?.[0]
            ?.description,
        propertyTypeId: data?.property?.propertyTypeId,
        propertyType2Id: data?.property?.propertyTypeId2,
        typology:
          data?.property?.propertyType2?.propertyTypeTranslations?.[0]
            ?.description,
        bedrooms: data?.property?.bedrooms,
        listingAgent: data?.property?.propertyAgents
          ? data?.property?.propertyAgents[0]?.user?.name
          : undefined,
        energeticCertification:
          data?.property?.energeticCertification?.description,
        grossArea: data?.property?.grossArea,
      };
    }

    if (!data?.externalProperty) {
      return undefined;
    }

    return {
      id: data.externalProperty.id,
      ref: data.externalProperty.reference,
      reference: data.externalProperty.reference,
      title: this.getExternalPropertyTitle(data.externalProperty),
      price: data.externalProperty.price,
      symbol: "€",
      image:
        data.externalProperty.imageFileName ??
        data.externalProperty.image ??
        undefined,
      isExternal: true,
      externalPropertyId: data.externalProperty.id,
      propertyTypeId: this.getExternalPropertyTypeId(data.externalProperty),
      propertyType: this.getExternalPropertyTypeDescription(
        data.externalProperty,
      ),
      typology: data.externalProperty.typology ?? null,
      agency: data.externalProperty.agency ?? null,
      address: data.externalProperty.address ?? null,
      zipCode: data.externalProperty.zipCode ?? null,
      districtId: data.externalProperty.districtId ?? null,
      countyId: data.externalProperty.countyId ?? null,
      parishId: data.externalProperty.parishId ?? null,
      agentId: data.externalProperty.agentId ?? null,
      agentName: data.externalProperty.agent?.name ?? null,
      location: {
        address: data.externalProperty.address ?? null,
        zipCode: data.externalProperty.zipCode ?? null,
        district: data.externalProperty.district?.name ?? null,
        county: data.externalProperty.county?.name ?? null,
        parish: data.externalProperty.parish?.name ?? null,
      },
    };
  }

  private mapExternalProperty(data: any): ExternalPropertyModel | undefined {
    if (!data?.externalProperty) {
      return undefined;
    }

    return {
      id: data.externalProperty.id,
      ref: data.externalProperty.reference,
      reference: data.externalProperty.reference,
      title:
        this.getExternalPropertyTitle(data.externalProperty) ||
        "Imóvel Externo",
      price: data.externalProperty.price,
      symbol: "€",
      image:
        data.externalProperty.imageFileName ??
        data.externalProperty.image ??
        undefined,
      isExternal: true,
      propertyTypeId: this.getExternalPropertyTypeId(data.externalProperty),
      propertyType: this.getExternalPropertyTypeDescription(
        data.externalProperty,
      ),
      typology: data.externalProperty.typology ?? null,
      agency: data.externalProperty.agency ?? null,
      address: data.externalProperty.address ?? null,
      zipCode: data.externalProperty.zipCode ?? null,
      districtId: data.externalProperty.districtId ?? null,
      countyId: data.externalProperty.countyId ?? null,
      parishId: data.externalProperty.parishId ?? null,
      agentId: data.externalProperty.agentId ?? null,
      agentName: data.externalProperty.agent?.name ?? null,
      location: {
        district: data.externalProperty.district?.name ?? null,
        county: data.externalProperty.county?.name ?? null,
        parish: data.externalProperty.parish?.name ?? null,
      },
    };
  }

  private normalizeFilterArray(values?: Array<string | number>): string[] {
    if (!values || !Array.isArray(values)) {
      return [];
    }

    return values
      .map((value) => `${value ?? ""}`.trim())
      .filter((value) => value.length > 0);
  }

  private appendDealsFilters(
    params: HttpParams,
    filters?: DealListFilters,
  ): HttpParams {
    if (!filters) {
      return params;
    }

    const appendArray = (key: keyof DealListFilters) => {
      const values = this.normalizeFilterArray(
        filters[key] as Array<string | number>,
      );
      values.forEach((value) => {
        params = params.append(`${key}`, value);
      });
    };

    const reference = `${filters?.reference ?? ""}`.trim();
    if (reference) {
      params = params.set("reference", reference);
    }

    appendArray("stores");
    appendArray("propertyStores");
    appendArray("associatedPropertyTypes");
    appendArray("associatedBedrooms");
    appendArray("interestLevels");
    appendArray("interestCountries");
    appendArray("interestRegions");
    appendArray("interestDistricts");
    appendArray("interestCounties");
    appendArray("interestParishes");
    appendArray("interestTypologies");
    appendArray("interestBedrooms");

    const createdAtFrom = `${filters?.createdAtFrom ?? ""}`.trim();
    if (createdAtFrom) {
      params = params.set("createdAtFrom", createdAtFrom);
    }

    const createdAtTo = `${filters?.createdAtTo ?? ""}`.trim();
    if (createdAtTo) {
      params = params.set("createdAtTo", createdAtTo);
    }

    const createdAtSort =
      filters?.createdAtSort === "asc" || filters?.createdAtSort === "desc"
        ? filters.createdAtSort
        : undefined;
    if (createdAtSort) {
      params = params.set("createdAtSort", createdAtSort);
    }

    if (filters?.archived) {
      params = params.set("archived", "true");
    }

    return params;
  }

  private appendDealsSearchParam(
    params: HttpParams,
    search?: string | null,
  ): HttpParams {
    const normalizedSearch = `${search ?? ""}`.trim();
    if (!normalizedSearch) {
      return params;
    }

    return params.set("search", normalizedSearch);
  }

  /***
   * Get All deals
   */
  findAllDealsBoard(
    lang: string,
    users: any[],
    type: DealDetailType,
    filters?: DealListFilters,
    options?: {
      statusId?: number | null;
      offset?: number;
      perStatusLimit?: number;
      search?: string | null;
    },
  ) {
    let params = new HttpParams();

    if (users && users?.length > 0)
      users.forEach((user) => {
        params = params.append("users", user?.id);
      });

    params = params.append("type", type);
    params = this.appendDealsFilters(params, filters);

    if (options?.statusId) {
      params = params.set("statusId", `${options.statusId}`);
    }
    if (options?.offset !== undefined) {
      params = params.set("offset", `${options.offset}`);
    }
    if (options?.perStatusLimit !== undefined) {
      params = params.set("perStatusLimit", `${options.perStatusLimit}`);
    }

    params = this.appendDealsSearchParam(params, options?.search);
    params = params.set("lang", lang);

    return this.http
      .get<DealsStatus[]>(`${environment.apiUrl}/deals/deals-board`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        // map((data) => this.mapDeals(data, type)),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  findAllDealsDetails(
    id: number,
    lang: any,
    type: DealDetailType,
  ) {
    const params = new HttpParams()
      .append("type", type)
      .set("lang", `${lang ?? ""}`);

    return this.http
      .get<Deals | null>(`${environment.apiUrl}/deals/deals-details/${id}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError(() => of(null)),
      );
  }

  getFiltersData(lang: string) {
    const params = new HttpParams().set("lang", lang);
    return this.http
      .get<DealListFiltersMetaData>(
        `${environment.apiUrl}/deals/meta/filters-data`,
        {
          params,
          withCredentials: true,
        },
      )
      .pipe(
        first(),
        catchError(() =>
          of({
            stores: [],
            agents: [],
            interestCountries: [],
            interestRegions: [],
            interestDistricts: [],
            interestCounties: [],
            interestParishes: [],
            interestTypologies: [],
          }),
        ),
      );
  }

  countFilteredDeals(
    users: any[],
    type: DealDetailType,
    view: DealListView,
    filters?: DealListFilters,
  ) {
    let params = new HttpParams().set("type", type).set("view", view);

    if (users && users?.length > 0) {
      users.forEach((user) => {
        params = params.append("users", user?.id);
      });
    }

    params = this.appendDealsFilters(params, filters);

    return this.http
      .get<{ total: number }>(
        `${environment.apiUrl}/deals/meta/filters-count`,
        {
          params,
          withCredentials: true,
        },
      )
      .pipe(
        first(),
        catchError(() => of({ total: 0 })),
      );
  }

  searchSaleDealsForAssociation(
    lang: string,
    options: {
      page?: number;
      pageSize?: number;
      search?: string;
      propertyId?: string;
      association?: "all" | "related" | "unrelated";
      statusId?: number | null;
      selectedDealId?: number | null;
    },
  ) {
    let params = new HttpParams();
    params = params.set("page", `${options?.page ?? 1}`);
    params = params.set("pageSize", `${options?.pageSize ?? 25}`);

    if (options?.search?.trim()) {
      params = params.set("search", options.search.trim());
    }
    if (options?.propertyId) {
      params = params.set("propertyId", options.propertyId);
    }
    if (options?.association && options.association !== "all") {
      params = params.set("association", options.association);
    }
    if (options?.statusId) {
      params = params.set("statusId", `${options.statusId}`);
    }
    if (
      options?.selectedDealId &&
      Number.isFinite(Number(options.selectedDealId)) &&
      Number(options.selectedDealId) > 0
    ) {
      params = params.set("selectedDealId", `${options.selectedDealId}`);
    }

    return this.http
      .get<DealAssociationSearchResponse>(
        `${environment.apiUrl}/deals/association/sale/${lang}`,
        {
          params,
          withCredentials: true,
        },
      )
      .pipe(
        first(),
        catchError(() =>
          of({
            items: [],
            total: 0,
            page: options?.page ?? 1,
            pageSize: options?.pageSize ?? 25,
            hasMore: false,
            statuses: [],
            selectedDeal: null,
          }),
        ),
      );
  }

  getActions(type: DealDetailType) {
    let params = new HttpParams().set("type", type);
    return this.http
      .get<any>(`${environment.apiUrl}/deals/deals-actions`, {
        withCredentials: true,
        params,
      })
      .pipe(first());
  }

  getValidations(type: DealDetailType) {
    const params = new HttpParams().set("type", type);
    return this.http
      .get<any>(`${environment.apiUrl}/deals/deals-validations`, {
        withCredentials: true,
        params,
      })
      .pipe(first());
  }

  /** Gatilhos "mover para cá quando…" (tipo `trigger`). */
  getTriggers(type: DealDetailType) {
    const params = new HttpParams().set("type", type);
    return this.http
      .get<any>(`${environment.apiUrl}/deals/deals-triggers`, {
        withCredentials: true,
        params,
      })
      .pipe(first());
  }

  getWonActions(type: DealDetailType) {
    const params = new HttpParams().set("type", type);
    return this.http
      .get<{ selectedActionId: number | null; actions: DealWonActionOption[] }>(
        `${environment.apiUrl}/deals/won-actions`,
        {
          withCredentials: true,
          params,
        },
      )
      .pipe(first());
  }

  getWonActionForDeal(
    id: any,
    type: DealDetailType,
  ): Observable<DealWonActionResolution> {
    const params = new HttpParams().set("type", type);
    return this.http
      .get<DealWonActionResolution>(
        `${environment.apiUrl}/deals/won-action/${id}`,
        {
          params,
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  getLostReasons(type: DealDetailType) {
    const params = new HttpParams().set("type", type);
    return this.http
      .get<any[]>(`${environment.apiUrl}/deals/lost-reasons`, {
        withCredentials: true,
        params,
      })
      .pipe(first());
  }

  createLostReason(data: {
    name: string;
    type: DealDetailType;
    fields?: {
      // Opcional: um campo novo ainda não tem chave, e o servidor deriva-a do
      // nome (`sanitizeLostReasonField`). Obrigá-la aqui levaria a inventar
      // chaves no cliente, que é onde elas deixariam de ser únicas.
      key?: string;
      name: string;
      translateKey?: string;
      type: "text" | "date" | "boolean" | "multichoice";
      required?: boolean;
      options?: string[];
      dependsOn?: { key: string; value: any };
    }[];
  }) {
    return this.http
      .post<any>(`${environment.apiUrl}/deals/lost-reasons`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateLostReason(
    id: number,
    data: {
      name: string;
      fields?: {
        key?: string;
        name: string;
        translateKey?: string;
        type: "text" | "date" | "boolean" | "multichoice";
        required?: boolean;
        options?: string[];
      }[];
    },
  ) {
    return this.http
      .patch<any>(`${environment.apiUrl}/deals/lost-reasons/${id}`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteLostReason(id: number) {
    return this.http
      .delete<any>(`${environment.apiUrl}/deals/lost-reasons/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /***
   * Get All Won deals
   */
  getAllWon(
    lang: any,
    users: any[],
    type: DealDetailType,
    filters?: DealListFilters,
    options?: {
      statusId?: number | null;
      offset?: number;
      perStatusLimit?: number;
      search?: string | null;
    },
  ) {
    let params = new HttpParams();
    if (users && users?.length > 0)
      users.forEach((user) => {
        params = params.append("users", user?.id);
      });

    params = params.append("type", type);
    params = this.appendDealsFilters(params, filters);

    if (options?.statusId) {
      params = params.set("statusId", `${options.statusId}`);
    }
    if (options?.offset !== undefined) {
      params = params.set("offset", `${options.offset}`);
    }
    if (options?.perStatusLimit !== undefined) {
      params = params.set("perStatusLimit", `${options.perStatusLimit}`);
    }

    params = this.appendDealsSearchParam(params, options?.search);
    params = params.set("lang", `${lang ?? ""}`);

    return this.http
      .get<DealsStatus[]>(`${environment.apiUrl}/deals/won`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        // map((data) => this.mapDeals(data, type)),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  /***
   * Get All Won deals
   */
  getAllLost(
    lang: any,
    users: any[],
    type: DealDetailType,
    filters?: DealListFilters,
    options?: {
      statusId?: number | null;
      offset?: number;
      perStatusLimit?: number;
      search?: string | null;
    },
  ) {
    let params = new HttpParams();
    if (users && users?.length > 0)
      users.forEach((user) => {
        params = params.append("users", user?.id);
      });

    params = params.append("type", type);
    params = this.appendDealsFilters(params, filters);

    if (options?.statusId) {
      params = params.set("statusId", `${options.statusId}`);
    }
    if (options?.offset !== undefined) {
      params = params.set("offset", `${options.offset}`);
    }
    if (options?.perStatusLimit !== undefined) {
      params = params.set("perStatusLimit", `${options.perStatusLimit}`);
    }

    params = this.appendDealsSearchParam(params, options?.search);
    params = params.set("lang", `${lang ?? ""}`);

    return this.http
      .get<DealsStatus[]>(`${environment.apiUrl}/deals/lost`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        // map((data) => this.mapDeals(data, type)),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  getAllProperties(page: number, search = "") {
    const lang = this._cookiesService.get("lang");
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/properties/${lang}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => {
          return {
            properties:
              data.properties.map((p: any) => {
                return {
                  id: p.id,
                  title: p.title,
                  reference: p.reference,
                  propertyType: p.propertyType,
                  businessType: p.businessType,
                  price: p.price,
                  isSelected: false,
                  isVisible: true,
                  file: `${environment.apiUrl}${environment.dirPropertiesImages}/${p.file}`,
                };
              }) || [],
            total: data.total,
          };
        }),
        catchError((error) => {
          return of({ properties: [], total: 0 });
        }),
      );
  }

  getContacts(page: number, search = "") {
    let params = new HttpParams().set("page", page);

    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/costumers`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of({ costumers: [], total: 0 });
        }),
      );
  }

  /**
   * Update deals
   * @param data
   * @returns
   */
  changeStatus(id: any, data: any, type: DealDetailType) {
    const lang = this._cookiesService.get("lang");

    return this.http
      .patch<any>(
        `${environment.apiUrl}/deals/change-status/${id}/${lang}?type=${type}`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Garante que a lead do negócio tem contacto: o servidor cria-o (ou
   * encontra-o por email/telefone) a partir dos dados da lead. Devolve
   * `{ contactId, created }`; 400 `t-deals-contact-data-missing` quando a lead
   * não tem nome, email nem telefone.
   */
  ensureLeadContact(leadId: any) {
    return this.http
      .patch<{ contactId: number; created: boolean }>(
        `${environment.apiUrl}/leads/${leadId}/contact/ensure`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  updateColumns(data: any, type: DealDetailType) {
    const params = new HttpParams().set("type", type);
    return this.http
      .patch<any>(`${environment.apiUrl}/deals/update-columns`, data, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  createDeal(data: any) {
    return this.http
      .post(`${environment.apiUrl}/deals`, data, { withCredentials: true })
      .pipe(first());
  }

  checkExistingLead(contactId: number, type: "sale" | "listing") {
    const params = new HttpParams().set("contactId", contactId).set("type", type);
    return this.http
      .get<{ lead: any }>(`${environment.apiUrl}/deals/check-existing-lead`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError(() => of({ lead: null })),
      );
  }

  createManualDeal(data: any, type: DealDetailType) {
    const params = new HttpParams().set("type", type);

    return this.http
      .post(`${environment.apiUrl}/deals/manual`, data, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getDealReferrals(
    dealId: number,
    type: DealDetailType,
  ): Observable<DealReferral[]> {
    const params = new HttpParams().set("type", type);
    return this.http
      .get<DealReferral[]>(`${environment.apiUrl}/deals/${dealId}/referrals`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError(() => of([])),
      );
  }

  getDealReferralTargets(): Observable<DealReferralTargetsResponse> {
    return this.http
      .get<DealReferralTargetsResponse>(
        `${environment.apiUrl}/deals/referrals/targets`,
        {
          withCredentials: true,
        },
      )
      .pipe(
        first(),
        catchError(() =>
          of({
            currentStoreId: null,
            stores: [],
          }),
        ),
      );
  }

  createDealReferral(
    dealId: number,
    data: {
      type: DealDetailType;
      toStoreId: number;
      toUserId?: string;
      notes?: string;
      commissionValue?: number | string;
    },
  ): Observable<DealReferral> {
    return this.http
      .post<DealReferral>(`${environment.apiUrl}/deals/${dealId}/referrals`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getSentReferrals(
    query?: {
      type?: DealDetailType;
      types?: string;
      status?:
        | "PENDING"
        | "ACCEPTED"
        | "REJECTED"
        | "CANCELED"
        | "EXPIRED"
        | "WON"
        | "LOST";
      statuses?: string;
      search?: string;
      consultantId?: string;
      storeId?: number;
      createdFrom?: string;
      createdTo?: string;
      page?: number;
      pageSize?: number;
    },
  ): Observable<{
    items: any[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  }> {
    let params = new HttpParams();
    if (query?.type) {
      params = params.set("type", query.type);
    }
    if (query?.status) {
      params = params.set("status", query.status);
    }
    if (query?.types) {
      params = params.set("types", query.types);
    }
    if (query?.statuses) {
      params = params.set("statuses", query.statuses);
    }
    if (query?.search) {
      params = params.set("search", query.search);
    }
    if (query?.consultantId) {
      params = params.set("consultantId", query.consultantId);
    }
    if (query?.storeId && query.storeId > 0) {
      params = params.set("storeId", query.storeId);
    }
    if (query?.createdFrom) {
      params = params.set("createdFrom", query.createdFrom);
    }
    if (query?.createdTo) {
      params = params.set("createdTo", query.createdTo);
    }
    if (query?.page && query.page > 0) {
      params = params.set("page", query.page);
    }
    if (query?.pageSize && query.pageSize > 0) {
      params = params.set("pageSize", query.pageSize);
    }

    return this.http
      .get<{
        items: any[];
        total: number;
        page: number;
        pageSize: number;
        hasMore: boolean;
      }>(`${environment.apiUrl}/deals/referrals/sent`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError(() =>
          of({
            items: [],
            total: 0,
            page: query?.page && query.page > 0 ? query.page : 1,
            pageSize:
              query?.pageSize && query.pageSize > 0 ? query.pageSize : 10,
            hasMore: false,
          }),
        ),
      );
  }

  createReferralFromContact(data: {
    contactId: number;
    type: "sale" | "listing";
    toStoreId: number;
    toUserId?: string;
    leadSource?: number;
    portal?: number;
    notes?: string;
    commissionValue?: number | string;
    propertyId?: string;
  }): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/deals/referrals/from-contact`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  acceptDealReferral(
    referralId: number,
    reason?: string,
  ): Observable<DealReferral> {
    return this.http
      .post<DealReferral>(
        `${environment.apiUrl}/deals/referrals/${referralId}/accept`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  rejectDealReferral(
    referralId: number,
    reason?: string,
  ): Observable<DealReferral> {
    return this.http
      .post<DealReferral>(
        `${environment.apiUrl}/deals/referrals/${referralId}/reject`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  deleteDeal(id: number) {
    return this.http
      .delete(`${environment.apiUrl}/deals/${id}`, { withCredentials: true })
      .pipe(first());
  }

  markAsLost(
    id: any,
    value: boolean,
    type: DealDetailType,
    options?: { reasonId?: number; reasonData?: Record<string, any> },
  ) {
    return this.http
      .patch(
        `${environment.apiUrl}/deals/mark-as-lost/${id}`,
        {
          value: value,
          type: type,
          reasonId: options?.reasonId,
          reasonData: options?.reasonData,
        },
        { withCredentials: true },
      )
      .pipe(first());
  }

  markAsWon(
    id: any,
    value: boolean,
    type: DealDetailType,
    options?: { saleDetails?: Record<string, any> },
  ) {
    return this.http
      .patch(
        `${environment.apiUrl}/deals/mark-as-won/${id}`,
        { value: value, type: type, saleDetails: options?.saleDetails },
        { withCredentials: true },
      )
      .pipe(first());
  }

  createSaleFromDeal(
    id: any,
    type: DealDetailType,
    saleDetails: Record<string, any>,
  ) {
    return this.http
      .patch(
        `${environment.apiUrl}/deals/create-sale/${id}`,
        { type, saleDetails },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Questionário de qualificação + score explicado por dimensão. */
  getLeadQualification(leadId: any) {
    return this.http
      .get<any>(`${environment.apiUrl}/deals/lead/${leadId}/qualification`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Grava uma resposta. `optionCode: null` limpa. Devolve o score atualizado. */
  saveLeadQualificationAnswer(
    leadId: any,
    questionCode: string,
    optionCode: string | null,
  ) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/deals/lead/${leadId}/qualification`,
        { questionCode, optionCode },
        { withCredentials: true },
      )
      .pipe(first());
  }

  getLeadScoreHistory(leadId: any) {
    return this.http
      .get<any>(`${environment.apiUrl}/deals/lead/${leadId}/score-history`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Acrescenta um imóvel à lista do negócio sem lhe mexer no alvo. */
  addSecondaryPropertyToDeal(
    dealId: any,
    data: { propertyId?: string; externalPropertyId?: number },
  ) {
    return this.http
      .post(`${environment.apiUrl}/deals/${dealId}/properties`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Troca o imóvel alvo do negócio. */
  setDealTargetProperty(
    dealId: any,
    data: { propertyId?: string; externalPropertyId?: number },
  ) {
    return this.http
      .patch(`${environment.apiUrl}/deals/${dealId}/properties/target`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Reunião com o proprietário (angariação): texto do email já preenchido
   * com os dados do negócio, para o utilizador editar antes de enviar.
   */
  getOwnerMeetingEmailPreview(
    dealId: any,
    query: { startAt?: string; location?: string } = {},
  ) {
    let params = new HttpParams();
    if (query.startAt) params = params.set("startAt", query.startAt);
    if (query.location) params = params.set("location", query.location);
    return this.http
      .get<any>(`${environment.apiUrl}/deals/listing/${dealId}/owner-meeting/email-preview`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Texto do email de confirmação de visita ao cliente, já preenchido, para editar. */
  getVisitEmailPreview(
    dealId: any,
    query: { startAt?: string; propertyId?: string; externalPropertyId?: number } = {},
  ) {
    let params = new HttpParams();
    if (query.startAt) params = params.set("startAt", query.startAt);
    if (query.propertyId) params = params.set("propertyId", query.propertyId);
    if (query.externalPropertyId) {
      params = params.set("externalPropertyId", `${query.externalPropertyId}`);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/deals/${dealId}/visits/email-preview`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Marca a reunião (atividade + agenda) e, se pedido, envia o email. */
  scheduleOwnerMeeting(
    dealId: any,
    data: {
      startAt: string;
      durationMinutes?: number;
      location?: string;
      notes?: string;
      /** A reunião é na morada do imóvel (quando realizada, vale como visita). */
      atProperty?: boolean;
      sendEmail?: boolean;
      emailSubject?: string;
      emailBody?: string;
    },
  ) {
    return this.http
      .post<any>(`${environment.apiUrl}/deals/listing/${dealId}/owner-meeting`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Agenda uma visita. Separado do /visit-form: marcar a visita deixou de
   * obrigar a gerar o PDF da ficha.
   */
  scheduleVisit(
    dealId: any,
    data: {
      startAt: string;
      durationMinutes?: number;
      propertyId?: string | null;
      externalPropertyId?: number | null;
      /** Vários imóveis: uma visita por imóvel, em sequência a partir de startAt. */
      properties?: Array<{ propertyId?: string | null; externalPropertyId?: number | null }>;
      /** Sem tipo o servidor procura o id na tabela das vendas. */
      type?: DealDetailType;
      /** Confirmação por email ao cliente (ligado por omissão). */
      sendClientEmail?: boolean;
      /** Texto editado no modal (vazio = template da imobiliária). */
      emailSubject?: string;
      emailBody?: string;
    },
  ) {
    return this.http
      .post(`${environment.apiUrl}/deals/${dealId}/visits`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Ciclo de vida do imóvel visto de dentro da angariação: referência
   * definitiva, envio para validação e o que falta segundo as regras do MASTER.
   */
  getListingPropertyState(dealId: number) {
    return this.http
      .get<any>(`${environment.apiUrl}/deals/listing/${dealId}/property/state`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Anexa um documento obrigatório (tipado) directamente ao imóvel da
   * angariação — vai para a ficha do imóvel, não para os ficheiros do negócio.
   */
  uploadListingPropertyDocument(
    dealId: number,
    documentTypeId: number,
    file: File,
    documentVisibility?: string | null,
  ) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("propertyDocumentTypeId", `${documentTypeId}`);
    if (documentVisibility) {
      formData.append("documentVisibility", documentVisibility);
    }
    return this.http
      .post<any>(
        `${environment.apiUrl}/deals/listing/${dealId}/property/documents`,
        formData,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Classifica um ficheiro do negócio como documento do imóvel: o servidor
   * copia-o para os documentos do imóvel com o tipo escolhido.
   */
  classifyListingDealFile(
    dealId: number,
    dealFileId: number,
    documentTypeId: number,
    documentVisibility?: string | null,
  ) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/deals/listing/${dealId}/property/documents/from-deal-file/${dealFileId}`,
        {
          propertyDocumentTypeId: documentTypeId,
          ...(documentVisibility ? { documentVisibility } : {}),
        },
        { withCredentials: true },
      )
      .pipe(first());
  }

  generateListingDefinitiveReference(dealId: number) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/deals/listing/${dealId}/property/definitive-reference`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  sendListingPropertyToValidation(dealId: number) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/deals/listing/${dealId}/property/send-to-validation`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Imóvel já publicado com a angariação ainda aberta (dados antigos): fecha
   * como ganha pelo caminho do sistema (sem won-actions nem alterações ao imóvel).
   */
  markListingWonFromPublished(dealId: number) {
    return this.http
      .patch<{ won: boolean; reason?: string }>(
        `${environment.apiUrl}/deals/listing/${dealId}/won-from-published`,
        {},
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Conflitos na agenda do utilizador para a janela pedida. */
  checkVisitAvailability(startAt: string, durationMinutes: number) {
    const params = new HttpParams()
      .set("startAt", startAt)
      .set("durationMinutes", `${durationMinutes}`);

    return this.http
      .get<{ available: boolean; conflicts: any[] }>(
        `${environment.apiUrl}/deals/visits/availability`,
        { params, withCredentials: true },
      )
      .pipe(first());
  }

  /** Remove um imóvel da lista. Se era o alvo, o negócio fica sem imóvel. */
  removeDealProperty(dealId: any, dealPropertyId: any) {
    return this.http
      .delete(
        `${environment.apiUrl}/deals/${dealId}/properties/${dealPropertyId}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Retira o imóvel alvo sem pôr outro no lugar. Serve também o imóvel com
   * que a lead chegou do site, que não tem linha em deals_properties.
   */
  clearDealTargetProperty(dealId: any) {
    return this.http
      .delete(`${environment.apiUrl}/deals/${dealId}/properties/target`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Com imagem o pedido passa a multipart/form-data — o backend aceita ambos e
   * desserializa `externalProperty` quando chega como texto. Sem imagem
   * mantém-se o JSON de sempre, para não alterar o painel antigo.
   */
  addPropertyToDeal(
    propertyId: any,
    dealId: any,
    type: DealDetailType,
    externalProperty?: ExternalPropertyPayloadModel,
    image?: File | null,
    externalPropertyOperation: {
      externalPropertyId?: number | null;
      createExternalProperty?: boolean;
    } = {},
  ) {
    const url = `${environment.apiUrl}/deals/add-property/${dealId}`;

    if (image && externalProperty) {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("externalProperty", JSON.stringify(externalProperty));
      formData.append("image", image);
      if (externalPropertyOperation.externalPropertyId) {
        formData.append(
          "externalPropertyId",
          `${externalPropertyOperation.externalPropertyId}`,
        );
      }
      if (externalPropertyOperation.createExternalProperty) {
        formData.append("createExternalProperty", "true");
      }
      // propertyId fica de fora de propósito: em FormData um null viajaria
      // como a string "null" e o backend trataria-o como imóvel interno.
      return this.http
        .patch(url, formData, { withCredentials: true })
        .pipe(first());
    }

    return this.http
      .patch(
        url,
        { propertyId, type, externalProperty, ...externalPropertyOperation },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /***
   * Get lead
   */
  getLead(leadId: number) {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get<any>(`${environment.apiUrl}/leads/${leadId}/${lang}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapDataLeads(data)),
      );
  }

  mapDataLeads(deal: any, id = null): Deals {
    const data = deal?.lead ? deal.lead : deal;
    const history = data?.leadHistory.filter(
      (l: any) =>
        l?.responsibleChange ||
        l?.isDone === true ||
        l?.action?.translateKey === "note",
    );
    const nextActivities = data?.leadHistory.filter(
      (l: any) =>
        !l?.responsibleChange &&
        l?.isDone === false &&
        l?.action?.translateKey !== "note",
    );
    const proposalFiles = data.proposals
      ? data.proposals
          .flatMap((p: any) => p.proposalsHistory || [])
          .flatMap((h: any) => h.proposalFile || [])
          .map((f: any) => ({ ...f, fieldName: "PROPOSAL" }))
      : [];
    const allFiles = [...(deal?.dealFiles || []), ...proposalFiles];
    return {
      id: id ? id : data?.id,
      linkedSaleDealId: deal?.saleDealId ?? null,
      linkedListingDealId: deal?.listingDealId ?? null,
      leadId: data?.id,
      email: data?.email,
      name: data?.name,
      message: data?.message,
      phone: data?.phone,
      isWon: deal?.isWon,
      isLost: deal?.isLost,
      dateHour: data?.createdAt ?? deal?.createdAt,
      contactType: data?.contactType?.description,
      contactTypeTranslation:
        data?.contactType?.contactTypeTranslations[0]?.description,
      contact: data?.contact,
      proposals:
        data?.proposals?.length > 0
          ? data?.proposals.map((p: any) => this.mapProposal(p))
          : undefined,
      // agent: data?.user?.email,
      agent: data?.user
        ? {
            name: data?.user?.name,
            email: data?.user?.email,
            id: data?.user?.id,
            image: data?.user?.userFiles
              ? data?.user?.userFiles[0]?.fileName
              : undefined,
          }
        : undefined,
      property: data?.externalProperty ? undefined : this.mapLeadProperty(data),
      externalProperty: this.mapExternalProperty(data),
      leadHistory:
        history?.map((l: any) => {
          return {
            id: l?.id,
            action: l?.action,
            startDate: l?.startDate,
            endDate: l?.endDate,
            isDone: l?.isDone,
            doneDate: l?.doneDate,
            title: l?.title,
            notes: l?.notes,
            location: l?.location,
            updatedBy: l?.userUpdated?.name,
            updatedAt: l?.responsibleChange?.changedAt ?? l?.createdAt,
            responsibleChange: l?.responsibleChange
              ? {
                  changedAt: l?.responsibleChange?.changedAt,
                  from: l?.responsibleChange?.from ?? null,
                  to: l?.responsibleChange?.to ?? null,
                }
              : undefined,
          };
        }) ?? [],
      source:
        data?.leadSource?.translateKey === "t-sources.t-portal" && !data?.portal
          ? "t-sources.t-portal-without-name"
          : data?.leadSource?.translateKey || data?.leadSource?.name,
      portal: data?.portal ? data?.portal?.name : undefined,
      nextActivities:
        nextActivities?.map((l: any) => {
          return {
            id: l?.id,
            action: l?.action,
            startDate: l?.startDate,
            endDate: l?.endDate,
            isDone: l?.isDone,
            title: l?.title,
            doneDate: l?.doneDate,
            notes: l?.notes,
            location: l?.location,
            updatedBy: l?.userUpdated?.name,
            updatedAt: l?.responsibleChange?.changedAt ?? l?.createdAt,
            responsibleChange: l?.responsibleChange
              ? {
                  changedAt: l?.responsibleChange?.changedAt,
                  from: l?.responsibleChange?.from ?? null,
                  to: l?.responsibleChange?.to ?? null,
                }
              : undefined,
          };
        }) ?? [],
      files: allFiles
        ? allFiles?.map((file: any) => {
            return {
              id: file?.id,
              name: file.originalName,
              size: file.size,
              type: file.fieldName,
              fileName: file.fileName,
              createdAt: file.createdAt,
              mimeType: file.mimeType,
              description: file?.description,
            };
          })
        : undefined,
    };
  }

  mapProposal(proposal: any) {
    const property = proposal.property;
    const mainAg = property?.propertyAgents?.filter((ag: any) => ag?.isMain);
    const isRent = property?.propertyBusinessType?.description === "To Lease";
    const propertyMap = {
      id: property?.id,
      title: property?.propertyTranslations[0]?.title,
      reference: property?.reference,
      price: property?.price,
      image: property?.propertyFiles[0]?.fileName,
      propertyType:
        property?.propertyType?.propertyTypeTranslations[0]?.description,
      propertyType2:
        property?.propertyType2?.propertyTypeTranslations[0]?.description,
      businessType:
        property?.propertyBusinessType?.propertyBusinessTypeTranslations[0]
          ?.description,
      store: { name: property?.store?.name, id: property?.store?.id },
      mainAgent: mainAg && mainAg?.length > 0 ? this.mapAgent(mainAg[0]) : null,
      agents:
        property.propertyAgents && property.propertyAgents?.length > 0
          ? property.propertyAgents.map((a: any) => this.mapAgent(a))
          : [],
      isRent,
      saleCommission: property?.saleCommission,
      isPercentageCommission: property?.isPercentageCommission,
      entity: property?.entity?.name,
    };
    const proposalMap: ProposalModel = {
      ...proposal,
      property: undefined,
      history: proposal?.proposalsHistory?.map((h: any) => {
        return {
          id: h.id,
          proposalStatus:
            h?.proposalStatus?.proposalStatusTranslation[0]?.description,
          proposalStatusCode: h?.proposalStatus?.description,
          proposalStatusId: h?.proposalStatusId,
          value: h?.value,
          obs: h?.obs,
          date: h?.createdAt,
          icon: h?.proposalStatus?.icon,
          file: h?.proposalFile,
          user: h?.createdBy,
          originalStatus: h?.proposalStatus?.description,
          saleDivision: h?.saleDivision,
          requiresDocument: h?.requiresDocument,
          useDeadline: h?.useDeadline,
          documentDeadline: h?.documentDeadline,
          documentRequestedAt: h?.documentRequestedAt,
          documentDeliveredAt: h?.documentDeliveredAt,
          expiredAt: h?.expiredAt,
          documentPending:
            !!h?.requiresDocument &&
            !h?.documentDeliveredAt &&
            (!h?.proposalFile || h?.proposalFile.length === 0) &&
            !h?.expiredAt,
        };
      }),
      firstProposal: this.mapHistory(
        proposal?.proposalsHistory?.find(
          (f: any) => f.proposalStatus.description === "First Proposal",
        ),
      ),
      lastAction:
        proposal?.proposalsHistory?.length > 1
          ? this.mapHistory(proposal?.proposalsHistory[0])
          : undefined,
    };
    return { proposal: proposalMap, property: propertyMap };
  }

  mapAgent(agent: any) {
    return {
      id: agent?.user?.id,
      name: agent.user?.name,
      isListingAgent: agent?.isListingAgent,
      isSellingAgent: agent?.isSellingAgent,
      saleCommission: agent?.saleCommission
        ? agent?.saleCommission
        : agent?.user?.userEntity?.saleCommission,
      listingCommission: agent?.listingCommission
        ? agent?.listingCommission
        : agent?.user?.userEntity?.listingCommission,
      agentFile: agent?.user?.userFiles
        ? agent?.user?.userFiles[0]?.fileName
        : undefined,
    };
  }

  mapHistory(h: any) {
    return {
      id: h.id,
      proposalStatus:
        h?.proposalStatus?.proposalStatusTranslation[0]?.description,
      proposalStatusCode: h?.proposalStatus?.description,
      proposalStatusId: h?.proposalStatusId,
      value: h?.value,
      obs: h?.obs,
      date: h?.createdAt,
      icon: h?.proposalStatus?.icon,
      file: h?.proposalFile,
      user: h?.createdBy,
      originalStatus: h?.proposalStatus?.description,
      saleDivision: h?.saleDivision,
      requiresDocument: h?.requiresDocument,
      useDeadline: h?.useDeadline,
      documentDeadline: h?.documentDeadline,
      documentRequestedAt: h?.documentRequestedAt,
      documentDeliveredAt: h?.documentDeliveredAt,
      expiredAt: h?.expiredAt,
      documentPending:
        !!h?.requiresDocument &&
        !h?.documentDeliveredAt &&
        (!h?.proposalFile || h?.proposalFile.length === 0) &&
        !h?.expiredAt,
    };
  }

  mapLeadHistory(data: any): LeadHistory {
    return {
      id: data?.id,
      action: data?.action,
      startDate: data?.startDate,
      endDate: data?.endDate,
      title: data?.title,
      isDone: data?.isDone,
      doneDate: data?.doneDate,
      notes: data?.notes,
      location: data?.location,
      updatedBy: data?.userUpdated?.name,
      updatedAt: data?.responsibleChange?.changedAt ?? data?.createdAt,
      responsibleChange: data?.responsibleChange
        ? {
            changedAt: data?.responsibleChange?.changedAt,
            from: data?.responsibleChange?.from ?? null,
            to: data?.responsibleChange?.to ?? null,
          }
        : undefined,
    };
  }

  mapDeals(data: any[], type: DealDetailType): DealsStatus[] {
    return data.map((status: any) => {
      return {
        id: status.id,
        order: status.order,
        name: status.isDefault
          ? type === "sale"
            ? status?.dealStatusTranslations[0]?.name
            : type === "listing"
              ? status?.dealsStatusListingTranslation[0]?.name
              : status?.dealsStatusProcessTranslation[0]?.name
          : status.name,
        isDefault: status.isDefault,
        actions: this.mapActions(status),
        validations: this.mapValidations(status),
        deals:
          type === "sale"
            ? status.deals.map((deal: any) => {
                const lead = deal?.lead
                  ? this.mapDataLeads(deal, deal?.id)
                  : null;
                return lead;
              })
            : type === "listing"
              ? status.dealListing.map((deal: any) => {
                  const lead = deal?.lead
                    ? this.mapDataLeads(deal, deal?.id)
                    : null;
                  return lead;
                })
              : status.dealProcess.map((deal: any) => {
                  const lead = deal?.lead
                    ? this.mapDataLeads(deal, deal?.id)
                    : null;
                  return lead;
                }),
      };
    });
  }

  mapActions(deal: any): StatusActions[] {
    return (
      deal.dealStatusAction
        ?.filter((action: any) => !this.isValidationAction(action))
        ?.map((action: any) => {
          return {
            actionId: action?.dealAction?.id,
            actionKey: action?.dealAction?.translateKey,
            actionName: action?.dealAction?.name,
            typeId: action?.dealAction?.type?.id,
            typeTranslateKey: action?.dealAction?.type?.translateKey,
            typeName: action?.dealAction?.type?.name,
          };
        }) ?? []
    );
  }

  mapValidations(deal: any): StatusValidation[] {
    return (
      deal.dealStatusAction
        ?.filter((action: any) => this.isValidationAction(action))
        ?.map((action: any) => {
          return {
            actionId: action?.dealAction?.id,
            actionKey: action?.dealAction?.translateKey,
            actionName: action?.dealAction?.name,
            typeId: action?.dealAction?.type?.id,
            typeTranslateKey: action?.dealAction?.type?.translateKey,
            typeName: action?.dealAction?.type?.name,
          };
        }) ?? []
    );
  }

  private isValidationAction(action: any): boolean {
    const typeName = `${action?.dealAction?.type?.name ?? ""}`.toLowerCase();
    const actionName = `${action?.dealAction?.name ?? ""}`.toLowerCase();
    const actionKey = `${action?.dealAction?.translateKey ?? ""}`.toLowerCase();

    return (
      typeName.includes("validation") ||
      typeName.includes("valida") ||
      actionName.includes("validation") ||
      actionName.includes("valida") ||
      actionKey.includes("validation") ||
      actionKey.includes("valida")
    );
  }

  getUserAuth() {
    return this.authService.currentUserValue;
  }

  uploadFile(data: any, id: any, type: DealDetailType) {
    const params = new HttpParams().set("type", type);
    return this.http
      .post(`${environment.apiUrl}/deals/${id}/upload`, data, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  transferToProcessPipeline(id: any, type: "sale" | "listing") {
    return this.http
      .post<{
        id: number;
        created: boolean;
      }>(
        `${environment.apiUrl}/deals/processes/transfer/${id}`,
        { type },
        { withCredentials: true },
      )
      .pipe(first());
  }

  returnProcessDealToSalesPipeline(id: any, reason: string) {
    return this.http
      .patch<{
        success: boolean;
        saleDealId: number;
      }>(
        `${environment.apiUrl}/deals/processes/${id}/return-to-sales`,
        { reason },
        { withCredentials: true },
      )
      .pipe(first());
  }

  deleteFile(id: any) {
    return this.http
      .delete(`${environment.apiUrl}/deals/file/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  waitForSync(): Observable<void> {
    return this.syncSubject.asObservable(); // os outros componentes escutam isto
  }
}
